import {Fragment, useEffect, useState} from 'react';
import {PageHeading} from '~/components';
import {ProductSelection} from '~/components/our-wines/ProductSelectionComponent';
import {WinesBanner} from '~/components/our-wines/WinesBanner';
import {AboutUs} from '~/components/shared/AboutUs';
import {Spacer} from '~/styles';
import {sanityFetch, urlFor} from '../api/sanity';
import {IOurWinesProduct} from '../types/ourWines';
import {ISanityData} from '../types/sanity';
import styled, {css} from 'styled-components';
import {breakpoint, toRem} from '~/styles/styledUtilities';
import {Link} from '@remix-run/react';
import SVG from 'react-inlinesvg';
import {Swiper, SwiperSlide} from 'swiper/react';
import { Controller, Scrollbar, EffectFade, EffectCoverflow } from 'swiper';
import Arrow from '../../assets/images/arrow.svg';

const SvgBackground = styled.section<{linkBorderColor?: string, backgroundColor: string}>`
  position: relative;
  background-color: ${props => props.backgroundColor};
  svg {
    display: none;
    position: absolute;
    top: 0;
    left: 50%;
    bottom: 0;
    height: 100%;
    width: auto;
    transform: translateX(-50%);
    z-index: 1;
  }
  div {
    position: relative;
    z-index: 2;
    color: white;
  }
  h2 {
    font-family: 'Faxi';
    font-style: normal;
    font-weight: 400;
    font-size: 32px;
    line-height: 32px;
    /* or 100% */

    text-transform: capitalize;
  }
  p {
    font-family: 'General Sans';
    font-style: normal;
    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
    /* or 133% */
  }
  .belluga {
    font-size: 40px;
    font-weight: 400;
    line-height: 54px;
    margin-bottom: 24px;
    font-family: 'Belluga';
  }
  .generalSans {
    font-family: 'General Sans';
    font-style: normal;
    font-weight: 500;
    font-size: 22px;
    line-height: 28px;
    max-width: 500px;
  }
  a {
    color: white;
    text-decoration: none;
    display: flex;
    font-family: 'General Sans';
    justify-content: flex-start;
    align-items: center;
    gap: 10px;
    margin-top: 25px;
    span {
      display: flex;
      justify-content: center;
      align-items: center;
      border: 1px solid white;
      border-radius: 50%;
      width: 30px;
      height: 30px;
      svg {
        position: static;
        transform: none;
        width: 50%;
        height: auto;
        path {
          fill: white;
        }
      }
    }
  }
  ${breakpoint(
    768,
    css`
      background-color: transparent;
      svg {
        display: block;
      }
      h2 {
        /* width: 240px; */
      }
      p {
        /* width: 390px; */
      }
    `,
  )}
  ${breakpoint(
    1200,
    css`
      h2 {
        /* width: 360px; */
        font-weight: 400;
        font-size: 40px;
        line-height: 54px;
      }
      p {
        /* width: 390px; */
      }
    `,
  )}
    ${breakpoint(
    1366,
    css`
      h2 {
        /* width: ${toRem(370)}; */
        font-size: ${toRem(40)};
        line-height: ${toRem(54)};
      }
      p {
        /* width: ${toRem(455)}; */
        font-size: ${toRem(14)};
        line-height: ${toRem(24)};
      }
    `,
  )}
  .info {
    padding: 40px 24px;
    display: flex;
    flex-direction: column;
    gap: 45px;
    width: 100%;
    h2, .content {
      flex: 1 1 50%;
    }
    ${breakpoint(
      768,
      css`
        flex-direction: row;
        justify-content: space-between;
        flex-wrap: nowrap;
        padding: 105px 50px;
      `,
    )}
    ${breakpoint(
      1200,
      css`
        padding: 100px;
      `,
    )}
    ${breakpoint(
      1366,
      css`
        padding: ${toRem(100)};
      `,
    )}
  }
  .links {
    padding: 40px 24px;
    display: flex;
    flex-direction: column;
    gap: 32px;
    width: 100%;
    .small {
      font-weight: 500;
      font-size: 18px;
      line-height: 24px;
    }
    .large {
      position: relative;
      left: 0;
      font-size: 22px;
      line-height: 28px;
      &::after {
        content: '';
        height: 2px;
        width: 0;
        background-color: ${props => props.linkBorderColor};
        transition: width 0.2s ease;
        position: absolute;
        left: 0;
        top: 100%;
      }
    }
    a {
      justify-content: space-between;
      flex-wrap: wrap;
      border-bottom: 2px solid gray;
      position: relative;
      &:hover {
        .large {
          &::after {
            width: 100%;
          }
        }
      }
    }
    ${breakpoint(
      768,
      css`
        padding: 105px 50px;
        gap: 45px;
        .large {
          font-size: 26px;
          line-height: 44px;
        }
      `,
    )}
    ${breakpoint(
      1200,
      css`
        padding: 100px;
        gap: 60px;
        .large {
          font-size: 35px;
          line-height: 42px;
        }
      `,
    )}
    ${breakpoint(
      1366,
      css`
        padding: ${toRem(100)};
        gap: ${toRem(65)} .large {
          font-size: ${toRem(35)};
          line-height: ${toRem(42)};
        }
        .small {
          font-size: ${toRem(18)};
          line-height: ${toRem(22)};
        }
      `,
    )}
  }
`;

const ProductsGrid = styled.section`
  padding: 0 24px;
  h2 {
    font-size: 35px;
    font-weight: 500;
    line-height: 42px;
    font-family: 'General Sans';
  }
  p {
    font-size: 14px;
    font-weight: 500;
    line-height: 24px;
    margin-top: 15px;
    font-family: 'General Sans';
  }
  .grid-container {    
    display: grid;
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
    row-gap: 30px;
  }
  .desc {
    font-size: 14px;
    line-height: 22px;
    h2 {
      font-size: 14px;
      line-height: 22px;
    }
  }
  .grid-element { //ovde
    display: flex;
    flex-direction: column;
    gap: 20px;
    img {
      width: 100%;
      height: auto;
    }
    .name {
      font-family: 'General Sans';
      font-style: normal;
      font-weight: 500;
      font-size: 35px;
      line-height: 42px;
    }
    .desc {
      font-family: 'General Sans';
      font-style: normal;
      font-weight: 500;
      font-size: 14px;
      line-height: 24px;
    }
    a {
      font-family: 'General Sans';
      font-style: normal;
      font-weight: 500;
      font-size: 18px;
      line-height: 24px;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: 10px;
      color: black;
      text-decoration: none;
      margin-top: 20px;
      span {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        border: 1px solid black;
        display: flex;
        justify-content: center;
        align-items: center;
        svg {
          width: 50%;
          height: auto;
          path {
            fill: black;
          }
        }
      }
    }
  }
  ${breakpoint(768, css`
  h2 {
      font-size: 42px;
      line-height: 52px;
    }
    p {
      font-size: 14px;
      font-weight: 500;
      line-height: 24px;
      margin-top: 15px;
    }
  .grid-container {
      grid-template-columns: repeat(2, 1fr);
      column-gap: 20px;
    }
    .grid-element {
      img {
        height: 240px;
        object-fit: contain;
      }
      .name {
        font-size: 22px;
        line-height: 28px;
      }
    }
  `)}
  ${breakpoint(1366, css`
    padding: 0 ${toRem(100)};
    h2 {
      font-size: 60px;
      line-height: 75px;
    }
    p {
      font-size: 14px;
    font-weight: 500;
    line-height: 24px;
    margin-top: 15px;
    }
    .grid-container {
      grid-template-columns: repeat(3, 1fr);
      column-gap: ${toRem(30)};
    }
    .grid-element {
      img {
        height: ${toRem(240)};
        object-fit: contain;
      }
      .name {
        font-size: ${toRem(22)};
        line-height: ${toRem(28)};
      }
    }
  `)}
`;

const SwiperContainer = styled.section`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 0 24px;
  a {
      font-family: 'General Sans';
      font-style: normal;
      font-weight: 500;
      font-size: 18px;
      line-height: 24px;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: 10px;
      color: black;
      text-decoration: none;
      margin-top: 20px;
      span {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: #122A4E;
        svg {
          width: 50%;
          height: auto;
        }
      }
    }
  h2 {
    font-family: 'General Sans';
    font-style: normal;
    font-weight: 500;
    font-size: 35px;
    line-height: 42px;
    margin-bottom: 20px;
  }
  p {
    font-family: "General Sans";
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
    margin-bottom: 20px;
  }
  h3 {
    font-family: 'Belluga';
    font-style: normal;
    font-weight: 400;
    font-size: 56.2225px;
    line-height: 117px;
  }
  .desc {
    max-height: 110px;
    overflow: hidden;
    font-family: "General Sans";
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
  }
  .images-swiper {
    padding-bottom: 20px;
    .swiper-slide {
      display: flex;
      justify-content: center;
      align-items: center;
      img {
        width: 100%;
        height: auto
      }
    }
    .swiper-scrollbar {
      background-color: lightgray;
      .swiper-scrollbar-drag {
        background-color: black;
      }
    }
  }
  .info {
    width: 100%;
  }
  ${breakpoint(768, css`
  flex-wrap: nowrap;
  justify-content: space-between;
  padding: 0 50px;
  .info {
    width: 45%;
  }
  .images-swiper {
    flex: 1 0 45%;
    height: 500px;
    .swiper-scrollbar {
      left: 20%;
      right: 20%;
      width: auto;
    }
    .swiper-slide {
      width: auto;
      img {
        height: 100%;
        width: auto;
      }
      img {
        /* transition: transform .2s ease; */
      }
    }
    .swiper-slide-active {
      img {
        /* transform: scale(150%); */
      }
    }
  }
  `)}
  ${breakpoint(1200, css`
    padding: 100px;
    h2 {
      font-size: 60px;
      line-height: 75px;
    }
    .images-swiper {
      height: 500px;
    }
    /* img {
      height: 250px;
    } */
  `)}
  ${breakpoint(1366, css`
    padding: ${toRem(100)};
    h2 {
      font-size: ${toRem(60)};
      line-height: ${toRem(75)};
    }
    .images-swiper {
      height: 500px;
    }
    /* img {
      height: 250px;
    } */
  `)}
`

export const Paragraph = styled.p`
  font-family: 'General Sans';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;
  padding: 0 30px;
  margin-bottom: 20px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  `

const SubHeading = styled.h3`
padding: 0 30px;
  font-family: 'General Sans';
  font-style: normal;
  font-weight: 500;
  font-size: 35px;
  line-height: 42px;
  margin-bottom: 35px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`

const Quote = styled.section`
  position: relative;
  padding: 24px;
  svg {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    height: auto;
    width: 100%;
    z-index: 1;
  };
  p {
    position: relative;
    z-index: 2;
    font-family: 'General Sans';
    font-style: normal;
    font-weight: 400;
    font-size: 22px;
    line-height: 28px;
    span {
      font-weight: 500;
    }
  }
  ${breakpoint(768, css`
    padding: 50px;
  `)}
  ${breakpoint(1366, css`
    padding: ${toRem(50)} ${toRem(100)};
  `)}
`

export const MapModulesToUi = ({
  sanityData,
  shopifyData: {nodes},
}: {
  shopifyData: {nodes: any[]};
  sanityData: ISanityData;
}) => {
  const modules = sanityData.modules ? sanityData.modules : [];
  const [swiperControl, setSwiperControl] = useState<any>();
  
  return (
    <>
      {modules.map((module: any) => {
        if (module._type === "module.productsSlider") {
          // console.log(module)
        }
        return {
          ['module.imageCustom']: (
            <div key={module._key} style={{maxWidth: "800px", marginInline: "auto", padding: "30px"}}>
                <img
                src={module.Image ? urlFor(module.Image.asset._ref) : ''}
                alt=""
                style={{width: '100%', height: 'auto'}}
              />
            </div>
          ),
          ['module.quote']: (
            <Quote>
              <svg xmlns="http://www.w3.org/2000/svg" width="768" height="184" viewBox="0 0 768 184" fill="none">
                <path d="M691.249 18.6428C699.691 16.891 708.025 17.4016 716.362 21.4924C720.538 23.5411 724.39 25.8306 728.685 26.848C734.513 28.2285 742.971 27.0381 748.112 32.9639C750.303 35.4893 752.331 40.8236 754.214 44.1641C755.664 46.7361 755.428 52.8355 757.083 54.67C759.1 56.9045 761.603 68.0432 762.328 71.9075C765.416 88.3797 769.377 105.358 766.942 123.36C766.045 129.991 765.257 137.976 763.125 143.736C760.346 151.244 754.238 155.286 749.756 158.82C743.997 163.363 738.214 167.765 732.375 172.015C719.114 181.667 703.081 179.759 689.064 180.536C670.16 181.584 651.334 185.411 632.437 182.254C624.186 180.875 616.065 180.319 607.785 180.473C602.495 180.572 597.236 181.598 591.94 181.593C585.714 181.589 579.63 179.705 573.434 179.378C558.413 178.584 543.312 178.16 528.284 178.148C521.175 178.143 514.262 177.367 507.184 175.868C496.657 173.637 486.401 173.587 475.782 173.579L410.418 173.529C383.908 173.509 357.544 175.085 331.089 177.494C314.056 179.044 296.674 181.948 279.612 181.354C277.079 181.265 274.979 179.621 272.615 178.518C267.679 176.213 262.428 178.213 257.516 180.142C256.489 180.545 255.508 181.592 254.425 181.271C254.258 181.222 254.134 181.168 254.044 181.109C253.838 181.504 253.676 181.811 253.643 181.837C250.319 184.407 244.701 182.459 241.244 182.456C235.562 182.452 230.646 182.316 225.119 179.676C220.289 177.37 213.775 178.79 208.849 179.098C199.319 179.693 189.693 183.689 180.143 182.158C171.358 180.75 162.428 179.818 153.717 177.357C151.902 176.845 151.604 177.41 150.022 178.109C141.381 181.926 131.196 175.579 122.315 175.572C111.972 175.564 101.709 172.327 91.3402 172.152C80.0693 171.962 68.7928 172.135 57.5212 172.126C45.184 172.116 32.6918 174.674 20.4341 171.846C15.2851 170.658 6.94111 169.58 4.74176 159.632C2.79884 150.844 4.40033 141.644 4.40734 132.523C4.42196 113.481 3.97389 95.2529 1.89264 76.481C0.293411 62.0567 -1.93124 37.394 3.85056 24.9076C9.18771 13.3814 15.0093 4.91902 23.7608 3.1608C33.124 1.27969 42.4481 2.93814 51.8248 3.37104C74.5424 4.41985 97.1754 6.86516 119.922 6.88263C129.702 6.89014 139.274 7.37942 148.947 4.64066C157.027 2.35314 165.369 1.71013 173.532 1.20025C204.181 -0.714101 234.762 1.08787 265.398 1.33371C286.956 1.50669 308.379 3.98774 329.907 4.71675C339.976 5.0577 349.941 3.6629 359.997 3.67063C373.322 3.68086 386.239 8.27111 399.531 9.11007C424.703 10.6987 450.468 13.2284 475.623 10.8038C485.306 9.87049 494.921 7.92732 504.578 6.6749C529.397 3.45652 554.329 8.34841 579.143 8.36747L602.02 8.38504C614.886 8.39492 627.895 9.12042 640.742 7.53423C645.233 6.97973 649.759 7.25439 654.241 7.85908C660.8 8.7439 667.132 6.84618 673.637 8.44004C675.853 8.98307 691.255 11.5998 691.249 18.6428Z" fill="#DFDDD4"/>
              </svg>
              <p>
                {module.quote}
                {module.name && (<span> - {module.name}</span>)}
              </p>
            </Quote>
          ),
          ['module.paragraph']: (
            <Paragraph key={module._key} dangerouslySetInnerHTML={{__html: module.paragraph}}></Paragraph>
          ),
          ['module.subHeading']: (
            <SubHeading key={module._key}>{module.subheading}</SubHeading>
          ),
          ['module.productsSlider']: (
            <>
            <SwiperContainer key={module._key}>
              <div className="info">
                <h2>{module.title}</h2>
                <p>{module.desc}</p>
                <Swiper
                  slidesPerView={1}
                  
                  modules={[Controller, EffectFade]}
                  effect="fade"
                  setWrapperSize={true}
                  onSwiper={setSwiperControl}
                  allowTouchMove={false}
                  noSwiping={true}
                  style={{marginBlock: "40px"}}
                  fadeEffect={
                    {
                      crossFade: true
                    }
                  }
                >
                    {sanityData.productsSliderRefData &&
                    sanityData.productsSliderRefData.map((product: any, index: number) => 
                    <SwiperSlide key={product.store.slug.current} virtualIndex={index}>
                      <Link to={'/product/' + product.store.slug.current}>
                      <h3 style={{color: product.featuredColor.hex, marginLeft: "15px"}}>{product.store.title}</h3>
                      </Link>
                      <div className="desc" dangerouslySetInnerHTML={{__html: product.store.descriptionHtml}}></div>
                    </SwiperSlide>)}
                </Swiper>
                {(module.linkRoute && module.linkTitle) && (
                <Link to={module.linkRoute}>
                  <span>
                  <svg width="29" height="29" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M24.4312 13.7549L13.0482 2.38261L14.6198 0.8125L28.6888 14.8681L14.6198 28.9238L13.0482 27.3537L24.4373 15.9753H0.578125V13.7549H24.4312Z" fill="white"></path>
                                </svg>
                  </span>
                  {module.linkTitle}
                </Link>
                )}
              </div>
            <Swiper 
              spaceBetween={50}
              scrollbar
              modules={[Controller, Scrollbar]}
              controller={{control: swiperControl}}
              grabCursor={true}
              centeredSlides={true}
              slidesPerView={1}
              className="images-swiper"
            >
              {sanityData.productsSliderRefData &&
                sanityData.productsSliderRefData.map((product: any, index: number) => 
                <SwiperSlide key={product.store.slug.current} virtualIndex={index}>
                  <img src={product.store.previewImageUrl} alt="" />
                </SwiperSlide>)}
            </Swiper>
            </SwiperContainer>
            <Spacer></Spacer>
            </>
          ),
          ['module.productsGrid']: (
            <ProductsGrid key={module._key}>
            <h2>{module.title}</h2>
            <p>{module.text}</p>
            <div className="grid-container">
              {sanityData.productsGridRefData &&
                sanityData.productsGridRefData.map((product: any) => (
                  <div className="grid-element" key={product._rev}>
                    <img src={product.store.previewImageUrl} alt="" />
                    <p className="name">{product.store.title}</p>
                    <div className="desc"
                      dangerouslySetInnerHTML={{
                        __html: product.store.descriptionHtml,
                      }}
                    ></div>
                    <Link to={`/product/${product.store.slug.current}`}>
                      <span>
                      <svg width="29" height="29" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M24.4312 13.7549L13.0482 2.38261L14.6198 0.8125L28.6888 14.8681L14.6198 28.9238L13.0482 27.3537L24.4373 15.9753H0.578125V13.7549H24.4312Z" fill="currentColor"></path>
                      </svg>
                      </span>
                      See more
                    </Link>
                  </div>
                ))}
            </div>
            <Spacer></Spacer>
            </ProductsGrid>
          ),
          ['module.svgBackGroundBlock']: (
            <>
              <SvgBackground key={module._key} backgroundColor={module.bcgColor ? module.bcgColor.hex : '#DFDDD4'}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 3827 683"
                  height="683"
                  width="3827"
                >
                  <path
                    fill={module.bcgColor ? module.bcgColor.hex : '#DFDDD4'}
                    d="M3441.25 40.0512C3483.21 32.9885 3524.75 34.3699 3566.47 49.1038C3587.36 56.4829 3606.66 64.78 3628.1 68.3052C3657.2 73.0884 3699.27 68.118 3725.16 89.8962C3736.2 99.1775 3746.55 118.945 3756.09 131.286C3763.44 140.787 3762.56 163.55 3770.9 170.288C3781.05 178.496 3794.06 219.882 3797.85 234.248C3814.04 295.489 3834.59 358.562 3823.34 425.856C3819.19 450.643 3815.66 480.474 3805.32 502.088C3791.84 530.266 3761.62 545.724 3739.47 559.187C3711 576.49 3682.42 593.271 3653.54 609.488C3587.97 646.318 3508.02 640.206 3438.25 643.984C3344.15 649.078 3250.57 664.532 3156.3 653.942C3115.13 649.318 3074.66 647.753 3033.43 648.849C3007.08 649.55 2980.94 653.703 2954.56 654.02C2923.55 654.393 2893.16 647.75 2862.28 646.918C2787.43 644.9 2712.2 644.264 2637.35 645.164C2601.95 645.589 2567.48 643.131 2532.15 637.982C2479.61 630.325 2428.53 630.782 2375.64 631.417L2050.09 635.33C1918.05 636.917 1786.82 644.451 1655.18 655.092C1570.42 661.944 1483.99 673.863 1398.98 672.718C1386.36 672.548 1375.82 666.548 1363.99 662.58C1339.3 654.295 1313.24 662.084 1288.87 669.583C1283.77 671.152 1278.94 675.118 1273.53 673.991C1272.7 673.818 1272.07 673.623 1271.62 673.411C1270.62 674.894 1269.82 676.052 1269.66 676.149C1253.23 685.944 1225.16 679.03 1207.94 679.237C1179.64 679.577 1155.15 679.379 1127.49 669.881C1103.32 661.58 1070.95 667.287 1046.43 668.744C998.995 671.561 951.247 687.067 903.607 681.956C859.786 677.256 815.263 674.343 771.755 665.71C762.692 663.912 761.234 666.041 753.388 668.747C710.54 683.523 659.503 660.49 615.268 661.022C563.757 661.641 512.482 650.211 460.831 650.209C404.686 650.207 348.531 651.559 292.391 652.234C230.945 652.972 168.851 663.296 107.663 653.516C81.9602 649.408 40.35 645.912 28.9119 608.947C18.8075 576.293 26.3362 541.879 25.9274 507.864C25.0737 436.844 21.9552 368.888 10.6761 299.007C2.00919 245.31 -10.2708 153.468 17.9184 106.536C43.9397 63.2136 72.5229 31.2871 116.025 24.1807C162.567 16.5776 209.087 22.178 255.81 23.2044C369.007 25.691 481.852 33.3912 595.144 32.0295C643.855 31.444 691.55 32.6684 739.596 21.8472C779.726 12.8088 821.246 9.88733 861.878 7.47364C1014.43 -1.58866 1166.83 3.21366 1319.43 2.20874C1426.81 1.50162 1533.63 9.41107 1640.89 10.7795C1691.05 11.4195 1740.61 5.59242 1790.7 4.9904C1857.07 4.1927 1921.62 20.5022 1987.87 22.7973C2113.31 27.1435 2241.76 34.962 2366.93 24.3412C2415.11 20.2529 2462.9 12.4025 2510.94 7.12567C2634.4 -6.43437 2758.81 10.2464 2882.4 8.76094L2996.34 7.39139C3060.42 6.62117 3125.25 8.51096 3189.16 1.78924C3211.5 -0.560532 3234.05 0.179921 3256.41 2.15402C3289.11 5.04262 3320.56 -2.43233 3353.04 3.10409C3364.1 4.99035 3440.94 13.7836 3441.25 40.0512Z"
                  />
                </svg>
                <div
                  className="info"
                  style={{
                    color: module.textColor ? module.textColor.hex : 'black',
                  }}
                >
                  <h2
                    style={{
                      color: module.textColor ? module.textColor.hex : 'black',
                    }}
                  >
                    {module.title}
                    {module.label && <p style={{marginTop: '15px'}}>{module.label}</p>}
                  </h2>
                  {module.content &&
                    module.content.map(
                      (block: {
                        headingBelluga?: string;
                        headingGeneralSans?: string;
                        paragraph?: string;
                        link?: string;
                        _key: string;
                      }) => (
                        <>
                          <div className="content" key={block._key}>
                            {block.headingBelluga && (
                              <h3
                                className="belluga"
                                style={{
                                  color: module.textColor
                                    ? module.textColor.hex
                                    : 'black',
                                }}
                              >
                                {block.headingBelluga}
                              </h3>
                            )}
                            {block.headingGeneralSans && (
                              <h3
                                className="generalSans"
                                style={{
                                  color: module.textColor
                                    ? module.textColor.hex
                                    : 'black',
                                }}
                              >
                                {block.headingGeneralSans}
                              </h3>
                            )}
                            {block.paragraph && (
                              <p
                                style={{
                                  color: module.textColor
                                    ? module.textColor.hex
                                    : 'black',
                                }}
                                dangerouslySetInnerHTML={{
                                  __html:block.paragraph
                                }}
                              >
                              </p>
                            )}
                            {block.link && (
                              <Link
                                to={`${block.link}`}
                                style={{
                                  color: module.textColor
                                    ? module.textColor.hex
                                    : 'black',
                                }}
                              >
                                <span style={{borderColor: module.textColor
                                    ? module.textColor.hex
                                    : 'black'}}>
                                <svg width="29" height="29" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M24.4312 13.7549L13.0482 2.38261L14.6198 0.8125L28.6888 14.8681L14.6198 28.9238L13.0482 27.3537L24.4373 15.9753H0.578125V13.7549H24.4312Z" style={{fill: module.textColor
                                    ? module.textColor.hex
                                    : 'black'}} fill={module.textColor ? module.textColor.hex : 'black'}></path>
                                </svg>
                                </span>
                                See More
                              </Link>
                            )}
                          </div>
                        </>
                      ),
                    )}
                </div>
              </SvgBackground>
              <Spacer key={module._key + 1} />
            </>
          ),
          ['module.svgLinks']: (
            <>
              <SvgBackground key={module._key} linkBorderColor={module.textColor ? module.textColor.hex : 'black'} backgroundColor={module.bcgColor ? module.bcgColor.hex : '#DFDDD4'}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 3827 683"
                  height="683"
                  width="3827"
                >
                  <path
                    fill={module.bcgColor ? module.bcgColor.hex : '#DFDDD4'}
                    d="M3441.25 40.0512C3483.21 32.9885 3524.75 34.3699 3566.47 49.1038C3587.36 56.4829 3606.66 64.78 3628.1 68.3052C3657.2 73.0884 3699.27 68.118 3725.16 89.8962C3736.2 99.1775 3746.55 118.945 3756.09 131.286C3763.44 140.787 3762.56 163.55 3770.9 170.288C3781.05 178.496 3794.06 219.882 3797.85 234.248C3814.04 295.489 3834.59 358.562 3823.34 425.856C3819.19 450.643 3815.66 480.474 3805.32 502.088C3791.84 530.266 3761.62 545.724 3739.47 559.187C3711 576.49 3682.42 593.271 3653.54 609.488C3587.97 646.318 3508.02 640.206 3438.25 643.984C3344.15 649.078 3250.57 664.532 3156.3 653.942C3115.13 649.318 3074.66 647.753 3033.43 648.849C3007.08 649.55 2980.94 653.703 2954.56 654.02C2923.55 654.393 2893.16 647.75 2862.28 646.918C2787.43 644.9 2712.2 644.264 2637.35 645.164C2601.95 645.589 2567.48 643.131 2532.15 637.982C2479.61 630.325 2428.53 630.782 2375.64 631.417L2050.09 635.33C1918.05 636.917 1786.82 644.451 1655.18 655.092C1570.42 661.944 1483.99 673.863 1398.98 672.718C1386.36 672.548 1375.82 666.548 1363.99 662.58C1339.3 654.295 1313.24 662.084 1288.87 669.583C1283.77 671.152 1278.94 675.118 1273.53 673.991C1272.7 673.818 1272.07 673.623 1271.62 673.411C1270.62 674.894 1269.82 676.052 1269.66 676.149C1253.23 685.944 1225.16 679.03 1207.94 679.237C1179.64 679.577 1155.15 679.379 1127.49 669.881C1103.32 661.58 1070.95 667.287 1046.43 668.744C998.995 671.561 951.247 687.067 903.607 681.956C859.786 677.256 815.263 674.343 771.755 665.71C762.692 663.912 761.234 666.041 753.388 668.747C710.54 683.523 659.503 660.49 615.268 661.022C563.757 661.641 512.482 650.211 460.831 650.209C404.686 650.207 348.531 651.559 292.391 652.234C230.945 652.972 168.851 663.296 107.663 653.516C81.9602 649.408 40.35 645.912 28.9119 608.947C18.8075 576.293 26.3362 541.879 25.9274 507.864C25.0737 436.844 21.9552 368.888 10.6761 299.007C2.00919 245.31 -10.2708 153.468 17.9184 106.536C43.9397 63.2136 72.5229 31.2871 116.025 24.1807C162.567 16.5776 209.087 22.178 255.81 23.2044C369.007 25.691 481.852 33.3912 595.144 32.0295C643.855 31.444 691.55 32.6684 739.596 21.8472C779.726 12.8088 821.246 9.88733 861.878 7.47364C1014.43 -1.58866 1166.83 3.21366 1319.43 2.20874C1426.81 1.50162 1533.63 9.41107 1640.89 10.7795C1691.05 11.4195 1740.61 5.59242 1790.7 4.9904C1857.07 4.1927 1921.62 20.5022 1987.87 22.7973C2113.31 27.1435 2241.76 34.962 2366.93 24.3412C2415.11 20.2529 2462.9 12.4025 2510.94 7.12567C2634.4 -6.43437 2758.81 10.2464 2882.4 8.76094L2996.34 7.39139C3060.42 6.62117 3125.25 8.51096 3189.16 1.78924C3211.5 -0.560532 3234.05 0.179921 3256.41 2.15402C3289.11 5.04262 3320.56 -2.43233 3353.04 3.10409C3364.1 4.99035 3440.94 13.7836 3441.25 40.0512Z"
                  />
                </svg>
                <div className="links">
                  {module.links &&
                    module.links.map(
                      (link: {
                        label: string;
                        _key: string;
                        title: string;
                        url: string;
                      }) => (
                        <Link
                          to={link.url}
                          key={link._key}
                          style={{
                            color: module.textColor
                              ? module.textColor.hex
                              : 'black',
                            borderColor: module.textColor ? module.textColor.hex+'80' : '#00000080'
                          }}
                        >
                          <p className="large" style={{borderColor: module.textColor ? module.textColor.hex : 'black'}}>{link.label}</p>
                          <p className="small">{link.title}</p>
                        </Link>
                      ),
                    )}
                </div>
              </SvgBackground>
              <Spacer key={module._key + 1} />
            </>
          ),
          ['module.banner']: (
            <>
              <img
                key={module._key}
                src={module.Banner ? urlFor(module.Banner.asset._ref) : ''}
                alt=""
                style={{width: '100vw', height: 'auto'}}
              />
              <Spacer key={module._key + 1} />
            </>
          ),
          ['module.pageCta']: (
            <>
              <PageHeading
                key={module._key}
                keyVal={'module._key'}
                content={{
                  title: module.pageTitle || sanityData.title || 'Lepa Vida',
                  ctaLabel: module.ctaLabel,
                  ctaText: module.ctaText,
                  showSocials: module.showSocial
                }}
              />
              <Spacer key={module._key + 1} />
            </>
          ),
          ['module.winesBanner']: (
            <>
              <WinesBanner
                key={module._key}
                keyVal={'module._key1'}
                module={module}
              />
              <Spacer key={module._key + 1} />
            </>
          ),
          ['module.ourWinesProducts']: nodes.map(
            (product: IOurWinesProduct, index: number) => (
              <>
                <ProductSelection
                  key={index}
                  keyVal={product && product.title}
                  featuredColor={
                    sanityData.productFeaturedColors ? sanityData.productFeaturedColors[index] : ''
                  }
                  isRight={index % 2 === 0}
                  product={product}
                  isStaticImage={true}
                />
                <Spacer key={product && product.title + 1} />
              </>
            ),
          ),
          ['module.imageAside']: (
            <>
              <AboutUs
                key={module._key}
                keyVal={'module._ke2'}
                module={module}
              />
              <Spacer key={module._key + 1} />
            </>
          ),
        }[module._type as string];
      })}
    </>
  );
};
